import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TriStateSwitch, toTri } from "@/components/ui/tri-state-switch";
import { Building2, Compass, MapPin, Ruler, Euro, Calculator, Link2, Wrench, ChevronsUpDown, Building, User, StickyNote, TrendingDown, Upload, X } from "lucide-react";
import ScreenshotUploader from "@/components/property-dialog/ScreenshotUploader";
import GalleryGrid from "@/components/property-dialog/GalleryGrid";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";

// Small, thin-line "Airbnb-style" icon rendered before each field label.
const FieldIcon = ({ icon: Icon }) => (
  <Icon className="w-3.5 h-3.5 text-slate-500 mr-1.5 inline-block align-[-2px]" strokeWidth={1.5} />
);

const empty = {
  citta: "ROMA",
  macro_area: "",
  indirizzo: "",
  civico: "",
  piano: "",
  mq: "",
  prezzo: "",
  link: "",
  da_ristrutturare: "NI",
  ascensore: false,
  tipo_venditore: "AGENZIA",
  agenzia_nome: "",
  scostamento_manuale: "",
  note: "",
  gallery: [],
  attachments: [],
};

// Split a scraped/OCR compound address like "VIA ROMA, 12/B" into
// ("VIA ROMA", "12/B"). If no civic number is detected, returns [raw, ""].
const splitIndirizzo = (raw) => {
  if (!raw) return ["", ""];
  let s = String(raw).trim().replace(/\s+/g, " ").replace(/[,\s]+$/, "");
  const m = s.match(/^(.*?)[\s,]+(\d+\s*[A-Za-z]?(?:\s*\/\s*\d*[A-Za-z]?)?(?:\s+(?:bis|ter))?)\s*[,.]?\s*$/i);
  if (!m) return [s, ""];
  const street = m[1].trim().replace(/,+\s*$/, "").trim();
  let num = m[2].replace(/\s*\/\s*/g, "/").replace(/\s+/g, " ").toUpperCase();
  num = num.replace(/^(\d+)\s+([A-Z])$/, "$1$2");
  return [street, num];
};

const PropertyDialog = ({ open, onOpenChange, editing, onSaved, boardId, boardName = "", macroAreas = [] }) => {
  const { settings, maxAttachBytes } = useSettings();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  // Tracks whether the macro-area Select is showing the "Nuova…" free-text
  // input (user wants to create a macro-area that doesn't exist yet).
  const [newMacroMode, setNewMacroMode] = useState(false);
  const attachInputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      setForm({
        ...empty,
        ...editing,
        mq: editing.mq ?? "",
        prezzo: editing.prezzo ?? "",
        scostamento_manuale: editing.scostamento_manuale ?? "",
        da_ristrutturare: toTri(editing.da_ristrutturare),
        gallery: editing.gallery || [],
        attachments: editing.attachments || [],
      });
      setNewMacroMode(false);
    } else {
      // Priority: admin-configured default_citta first (that's its purpose),
      // then fall back to boardName only if settings hasn't loaded a value,
      // then to the hardcoded fallback. Board titles can be arbitrary
      // (e.g. "La mia prima scheda") so we can't trust them as city defaults.
      const defaultCitta = (settings?.default_citta || boardName || empty.citta).toUpperCase();
      setForm({ ...empty, citta: defaultCitta });
      setNewMacroMode(false);
    }
  }, [editing, open, boardName, settings?.default_citta]);

  const upd = (k, v) => {
    if (["citta", "macro_area", "indirizzo", "civico", "piano", "agenzia_nome"].includes(k) && typeof v === "string") {
      v = v.toUpperCase();
    }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        board_id: boardId,
        citta: form.citta.trim().toUpperCase(),
        macro_area: form.macro_area.trim().toUpperCase(),
        indirizzo: form.indirizzo.trim(),
        civico: (form.civico || "").trim(),
        piano: String(form.piano || ""),
        mq: parseFloat(form.mq),
        prezzo: parseFloat(form.prezzo),
        link: form.link || "",
        da_ristrutturare: toTri(form.da_ristrutturare),
        ascensore: !!form.ascensore,
        tipo_venditore: form.tipo_venditore,
        agenzia_nome: form.tipo_venditore === "AGENZIA" ? (form.agenzia_nome || "") : "",
        scostamento_manuale: form.scostamento_manuale === "" || form.scostamento_manuale === null ? null : parseFloat(form.scostamento_manuale),
        note: form.note || "",
        gallery: form.gallery || [],
        attachments: form.attachments || [],
      };
      if (editing) {
        await api.put(`/properties/${editing.id}`, payload);
        toast.success("Immobile aggiornato");
      } else {
        await api.post("/properties", payload);
        toast.success("Immobile aggiunto");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const eurMq = form.mq && form.prezzo ? (parseFloat(form.prezzo) / parseFloat(form.mq)) : 0;

  // Merge AI-extracted fields into the current form state.
  const onAIExtracted = ({ extracted = {}, gallery = [] }) => {
    // Backend already splits `indirizzo`+`civico`, but as a safety net if the
    // AI ignores the schema we still try to split client-side.
    let ind = (extracted.indirizzo || "").toUpperCase();
    let civ = (extracted.civico || "").toUpperCase();
    if (ind && !civ) {
      const [s, n] = splitIndirizzo(ind);
      ind = s; civ = n;
    }
    setForm((f) => ({
      ...f,
      citta: (extracted.citta || f.citta || "").toUpperCase(),
      macro_area: (extracted.macro_area || f.macro_area || "").toUpperCase(),
      indirizzo: ind || f.indirizzo || "",
      civico: civ || f.civico || "",
      piano: extracted.piano ?? f.piano ?? "",
      mq: extracted.mq ?? f.mq ?? "",
      prezzo: extracted.prezzo ?? f.prezzo ?? "",
      link: extracted.link || f.link || "",
      da_ristrutturare: toTri(extracted.da_ristrutturare ?? f.da_ristrutturare),
      ascensore: typeof extracted.ascensore === "boolean" ? extracted.ascensore : f.ascensore,
      tipo_venditore: extracted.tipo_venditore || f.tipo_venditore,
      agenzia_nome: (extracted.agenzia_nome || f.agenzia_nome || "").toUpperCase(),
      note: extracted.note ? (f.note ? f.note + "\n" + extracted.note : extracted.note) : f.note,
      gallery: [...(f.gallery || []), ...gallery],
    }));
  };

  const removeGalleryImage = (idx) => {
    setForm((f) => ({ ...f, gallery: (f.gallery || []).filter((_, i) => i !== idx) }));
  };

  // --- Attachments (PDF / Word / Excel / images / etc.) --------------------
  // Per-file cap is admin-configurable via /admin/settings → max_attachment_mb.
  const MAX_ATTACH_BYTES = maxAttachBytes;
  const MAX_ATTACH_MB = Math.round(maxAttachBytes / (1024 * 1024));
  const MAX_ATTACH_COUNT = 12;

  const onAttachPicked = async (files) => {
    if (!files || files.length === 0) return;
    const current = form.attachments || [];
    const remaining = Math.max(0, MAX_ATTACH_COUNT - current.length);
    const list = Array.from(files).slice(0, remaining);
    if (files.length > remaining) toast.warning(`Puoi caricare al massimo ${MAX_ATTACH_COUNT} allegati per immobile`);
    const readOne = (file) => new Promise((resolve, reject) => {
      if (file.size > MAX_ATTACH_BYTES) return reject(new Error(`"${file.name}" supera ${MAX_ATTACH_MB} MB`));
      const r = new FileReader();
      r.onload = () => resolve({
        name: file.name,
        mime: file.type || "application/octet-stream",
        size: file.size,
        data_url: r.result,
      });
      r.onerror = () => reject(r.error || new Error("Errore lettura file"));
      r.readAsDataURL(file);
    });
    for (const f of list) {
      try {
        const doc = await readOne(f);
        setForm((prev) => ({ ...prev, attachments: [...(prev.attachments || []), doc] }));
      } catch (err) {
        toast.error(err.message || `Impossibile caricare ${f.name}`);
      }
    }
  };
  const removeAttachment = (idx) => setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((_, i) => i !== idx) }));
  const fmtSize = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`;
  const attachIcon = (mime = "") => {
    if (mime.startsWith("image/")) return "🖼️";
    if (mime === "application/pdf") return "📕";
    if (mime.includes("word") || mime.includes("officedocument.wordprocessingml")) return "📘";
    if (mime.includes("sheet") || mime.includes("excel") || mime.includes("officedocument.spreadsheetml")) return "📗";
    if (mime.includes("presentation") || mime.includes("powerpoint")) return "📙";
    if (mime.includes("zip") || mime.includes("compressed")) return "🗜️";
    if (mime.startsWith("text/")) return "📄";
    return "📎";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-2xl border border-slate-200 bg-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tight">
            {editing ? "Modifica immobile" : "Nuovo immobile"}
          </DialogTitle>
        </DialogHeader>

        {/* AI screenshot uploader — solo in creazione */}
        {!editing && <ScreenshotUploader onExtracted={onAIExtracted} />}

        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-1">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={Building2} />Città *</Label>
            <Input data-testid="pd-citta" required value={form.citta} onChange={(e) => upd("citta", e.target.value)} className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]" />
          </div>
          <div className="col-span-1">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={Compass} />Macro area *</Label>
            {newMacroMode ? (
              <div className="flex items-center gap-1 mt-1">
                <Input
                  data-testid="pd-macro-new"
                  autoFocus
                  required
                  value={form.macro_area}
                  onChange={(e) => upd("macro_area", e.target.value)}
                  placeholder="Nome nuova macro area"
                  className="rounded-xl bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-full h-9 px-2 text-[11px] text-slate-500"
                  onClick={() => { setNewMacroMode(false); upd("macro_area", ""); }}
                  data-testid="pd-macro-cancel"
                  title="Torna all'elenco"
                >
                  ← Elenco
                </Button>
              </div>
            ) : (
              <Select
                value={form.macro_area || "__none"}
                onValueChange={(v) => {
                  if (v === "__new") { setNewMacroMode(true); upd("macro_area", ""); return; }
                  if (v === "__none") { upd("macro_area", ""); return; }
                  upd("macro_area", v);
                }}
              >
                <SelectTrigger data-testid="pd-macro" className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]">
                  <SelectValue placeholder="Seleziona macro area" />
                </SelectTrigger>
                <SelectContent>
                  {macroAreas.length === 0 && (
                    <SelectItem value="__none" disabled>Nessuna macro area presente</SelectItem>
                  )}
                  {macroAreas.filter((m) => m && String(m).trim() !== "").map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                  <SelectItem value="__new" data-testid="pd-macro-option-new">
                    <span className="inline-flex items-center gap-1.5 text-[#007AFF] font-semibold">＋ Nuova…</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="col-span-2 grid grid-cols-[1fr_140px] gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={MapPin} />Indirizzo (via/piazza) *</Label>
              <Input data-testid="pd-indirizzo" required value={form.indirizzo} onChange={(e) => upd("indirizzo", e.target.value)} placeholder="Es. VIA GIOVANNI MICHELOTTI" className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">N. civico</Label>
              <Input data-testid="pd-civico" value={form.civico || ""} onChange={(e) => upd("civico", e.target.value)} placeholder="Es. 12/B" className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF] text-center font-mono-num" />
            </div>
          </div>
          <div className="col-span-1">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={Building} />Piano</Label>
            <Input data-testid="pd-piano" value={form.piano} onChange={(e) => upd("piano", e.target.value)} placeholder="Es. 3, Rialzato, Attico" className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]" />
          </div>
          <div className="col-span-1">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={Ruler} />MQ *</Label>
            <Input data-testid="pd-mq" required type="number" step="0.1" value={form.mq} onChange={(e) => upd("mq", e.target.value)} className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]" />
          </div>
          <div className="col-span-1">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={Euro} />Prezzo (€) *</Label>
            <Input data-testid="pd-prezzo" required type="number" step="1" value={form.prezzo} onChange={(e) => upd("prezzo", e.target.value)} className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]" />
          </div>
          <div className="col-span-1">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={Calculator} />€/MQ (calcolato)</Label>
            <Input data-testid="pd-eurmq" value={eurMq ? eurMq.toFixed(2) : ""} disabled className="rounded-none mt-1 bg-slate-100 font-mono-num" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={Link2} />Link annuncio</Label>
            <Input data-testid="pd-link" value={form.link} onChange={(e) => upd("link", e.target.value)} placeholder="https://www.immobiliare.it/..." className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]" />
          </div>
          <div className="col-span-1">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={User} />Tipo venditore</Label>
            <Select value={form.tipo_venditore} onValueChange={(v) => upd("tipo_venditore", v)}>
              <SelectTrigger data-testid="pd-tipo" className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="AGENZIA">Agenzia</SelectItem>
                <SelectItem value="PRIVATO">Privato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={Building2} />Nome agenzia</Label>
            <Input data-testid="pd-agenzia" value={form.agenzia_nome} onChange={(e) => upd("agenzia_nome", e.target.value)} disabled={form.tipo_venditore !== "AGENZIA"} className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]" />
          </div>
          <div className="col-span-1 flex items-center justify-between rounded-xl bg-slate-100 px-3 py-2 mt-2">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={Wrench} />Ristrutturato</Label>
            <TriStateSwitch
              testId="pd-rist"
              value={form.da_ristrutturare}
              onValueChange={(v) => upd("da_ristrutturare", v)}
            />
          </div>
          <div className="col-span-1 flex items-center justify-between rounded-xl bg-slate-100 px-3 py-2 mt-2">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={ChevronsUpDown} />Ascensore</Label>
            <Switch data-testid="pd-asc" checked={form.ascensore} onCheckedChange={(v) => upd("ascensore", v)} />
          </div>
          <div className="col-span-2 border-t border-slate-200 pt-4">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={StickyNote} />Note</Label>
            <textarea
              data-testid="pd-note"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              rows={3}
              maxLength={2000}
              placeholder="Appunti liberi (esposizione, riscaldamento, spese condominio, prima visita, ecc.)"
              className="w-full rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF] focus:outline-none px-3 py-2 text-sm resize-none"
            />
            <p className="text-[11px] text-slate-500 mt-1">{(form.note || "").length}/2000</p>
          </div>
          <div className="col-span-2 border-t border-slate-200 pt-4">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={TrendingDown} />Scostamento manuale (opzionale)</Label>
            <Input
              data-testid="pd-scost"
              type="number"
              step="0.001"
              value={form.scostamento_manuale}
              onChange={(e) => upd("scostamento_manuale", e.target.value)}
              placeholder="Lascia vuoto per calcolo automatico (es. -0.15 = -15%)"
              className="rounded-none mt-1 font-mono-num"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Se vuoto, lo scostamento viene calcolato automaticamente come variazione % di €/MQ rispetto alla media €/m² del filtro corrente.
            </p>
          </div>
          <DialogFooter className="col-span-2 pt-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" data-testid="pd-save" disabled={saving} className="rounded-full bg-[#007AFF] hover:bg-[#0060df]">
              {saving ? "Salvataggio…" : editing ? "Aggiorna" : "Aggiungi"}
            </Button>
          </DialogFooter>
        </form>

        {/* Galleria foto dell'immobile */}
        <GalleryGrid images={form.gallery} onRemove={removeGalleryImage} />

        {/* Allegati (PDF, Word, Excel, immagini, ecc.) */}
        <div className="border-t border-slate-200 pt-4 mt-2" data-testid="pd-attachments">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wider"><FieldIcon icon={Upload} />Allegati ({(form.attachments || []).length})</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => attachInputRef.current?.click()}
              data-testid="pd-attach-upload-btn"
              className="rounded-full h-8 border-slate-300"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Aggiungi file
            </Button>
            <input
              ref={attachInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,image/*"
              className="hidden"
              onChange={(e) => { onAttachPicked(e.target.files); e.target.value = ""; }}
              data-testid="pd-attach-input"
            />
          </div>
          {(form.attachments || []).length === 0 ? (
            <p className="text-[11px] text-slate-500 mt-2">Nessun allegato — carica PDF, Word, Excel, immagini aggiuntive o altri file (max {MAX_ATTACH_MB} MB ciascuno, fino a {MAX_ATTACH_COUNT} file).</p>
          ) : (
            <ul className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden" data-testid="pd-attach-list">
              {(form.attachments || []).map((a, i) => (
                <li key={i} className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-slate-50" data-testid={`pd-attach-${i}`}>
                  <span className="text-lg leading-none">{attachIcon(a.mime)}</span>
                  <a
                    href={a.data_url}
                    download={a.name}
                    className="flex-1 min-w-0 text-xs font-medium text-slate-800 hover:text-[#007AFF] truncate"
                    title={`${a.name} · ${fmtSize(a.size)}`}
                    data-testid={`pd-attach-link-${i}`}
                  >
                    {a.name}
                  </a>
                  <span className="text-[10px] text-slate-500 font-mono-num shrink-0">{fmtSize(a.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(i)}
                    data-testid={`pd-attach-remove-${i}`}
                    className="w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                    aria-label="Rimuovi allegato"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyDialog;
